import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import GlobalLayout from '../GlobalLayout';
import { getApps, getPage, getLayout, getQuery, getQueries, updatePage } from '../../services/admin_api';

const createEmptyContentRow = () => ({
    type: '',
    view: '',
    queryId: '',
    queryName: '',
    params: '{\n  "title": ""\n}'
});

const mapContentsToRows = (contents) => {
    const contentList = Array.isArray(contents)
        ? contents
        : (Array.isArray(contents?.content) ? contents.content : []);

    return contentList.map((item) => {
        const legacyQuery = item?.query ?? '';
        const legacyQueryAsString = String(legacyQuery || '');
        const hasLegacyNumericQuery = /^\d+$/.test(legacyQueryAsString);

        return ({
            type: item?.type || '',
            view: item?.view || '',
            queryId: item?.queryId ?? (hasLegacyNumericQuery ? Number(legacyQueryAsString) : ''),
            queryName: item?.queryName || (hasLegacyNumericQuery ? '' : legacyQueryAsString),
            params: item?.params ? JSON.stringify(item.params, null, 2) : '{\n  "title": ""\n}'
        });
    });
};

const buildPageManagerListPath = (appId) => {
    if (!appId) {
        return '/pages';
    }

    return `/pages?appId=${encodeURIComponent(String(appId))}`;
};

const getQueryAppId = (queryItem) => queryItem?.app_id ?? queryItem?.appId ?? queryItem?.app?.id ?? '';

const getQueryOptionType = (queryItem) => queryItem?.category || queryItem?.type || '';

const getAppDisplayLabel = (app) => app?.label || app?.name || app?.code || (app?.id ? `App #${app.id}` : 'App inconnue');

const getQueryDisplayLabel = (queryItem) => queryItem?.label || queryItem?.name || queryItem?.code || `Query #${queryItem?.id}`;

const buildQueryOptionLabel = (queryItem, appLabel) => {
    const queryLabel = getQueryDisplayLabel(queryItem);

    if (!appLabel) {
        return queryLabel;
    }

    return `${appLabel} / ${queryLabel}`;
};

const mapQueryToOption = (queryItem, apps = []) => {
    const appId = getQueryAppId(queryItem);
    const matchedApp = queryItem?.app || apps.find((app) => String(app.id) === String(appId));
    const appLabel = matchedApp ? getAppDisplayLabel(matchedApp) : (appId ? `App #${appId}` : '');

    return {
        id: queryItem.id,
        value: String(queryItem.id),
        name: queryItem.name || queryItem.code || String(queryItem.id),
        queryLabel: getQueryDisplayLabel(queryItem),
        label: buildQueryOptionLabel(queryItem, appLabel),
        category: getQueryOptionType(queryItem),
        appId,
        appLabel
    };
};

const sortQueryOptions = (queryOptions) => [...queryOptions].sort((firstOption, secondOption) => {
    const firstHasAppLabel = Boolean(firstOption.appLabel);
    const secondHasAppLabel = Boolean(secondOption.appLabel);

    if (firstHasAppLabel !== secondHasAppLabel) {
        return firstHasAppLabel ? -1 : 1;
    }

    const appLabelComparison = (firstOption.appLabel || '').localeCompare(secondOption.appLabel || '', 'fr', { sensitivity: 'base' });

    if (appLabelComparison !== 0) {
        return appLabelComparison;
    }

    return (firstOption.queryLabel || '').localeCompare(secondOption.queryLabel || '', 'fr', { sensitivity: 'base' });
});

const getGroupedQueryOptions = (queryOptions) => Array.from(
    queryOptions.reduce((groups, queryOption) => {
        const groupLabel = queryOption.appLabel || 'Sans app';

        if (!groups.has(groupLabel)) {
            groups.set(groupLabel, []);
        }

        groups.get(groupLabel).push(queryOption);
        return groups;
    }, new Map()).entries()
).map(([appLabel, options]) => ({ appLabel, options }));

const getAvailableQueryOptions = (queryOptions, selectedType, selectedQueryId) => {
    const filteredOptions = selectedType
        ? queryOptions.filter((option) => getQueryOptionType(option) === selectedType)
        : queryOptions;

    if (selectedQueryId === '' || selectedQueryId === null || selectedQueryId === undefined) {
        return filteredOptions;
    }

    if (filteredOptions.some((option) => String(option.id) === String(selectedQueryId))) {
        return filteredOptions;
    }

    const selectedOption = queryOptions.find((option) => String(option.id) === String(selectedQueryId));
    return selectedOption ? [...filteredOptions, selectedOption] : filteredOptions;
};

const getDefaultViewForType = (type, currentView = '') => {
    if (type === 'indic') return 'indic';
    if (currentView && currentView !== 'indic') return currentView;
    if (type === 'grid') return 'ssrm';
    if (type === 'chart') return 'bar';

    return '';
};

const PageManagerEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [layouts, setLayouts] = useState([]);
    const [queryOptions, setQueryOptions] = useState([]);
    const [selectedLayoutIndex, setSelectedLayoutIndex] = useState('');
    const [originalPage, setOriginalPage] = useState(null);
    const [contentsRows, setContentsRows] = useState([]);
    const [formData, setFormData] = useState({
        label: '',
        layout_id: '',
        layout: '',
        url: ''
    });

    const getReturnPath = () => buildPageManagerListPath(
        searchParams.get('appId')
        || originalPage?.app_id
        || originalPage?.appId
        || originalPage?.app?.id
        || ''
    );

    useEffect(() => {
        const loadData = async () => {
            try {
                const [pageResponse, layoutResponse, queriesResponse, appsResponse] = await Promise.all([
                    getPage(id),
                    getLayout(),
                    getQueries(1, 1000),
                    getApps(1, 1000)
                ]);

                const page = pageResponse?.data || pageResponse;
                const rawLayouts = layoutResponse?.data || layoutResponse;
                const rawQueries = queriesResponse?.data || queriesResponse;
                const rawApps = appsResponse?.data || appsResponse;
                const fetchedLayouts = Array.isArray(rawLayouts)
                    ? rawLayouts
                    : (Array.isArray(rawLayouts?.data) ? rawLayouts.data : []);
                const fetchedQueries = Array.isArray(rawQueries)
                    ? rawQueries
                    : (Array.isArray(rawQueries?.data) ? rawQueries.data : []);
                const fetchedApps = Array.isArray(rawApps)
                    ? rawApps
                    : (Array.isArray(rawApps?.data) ? rawApps.data : []);

                const mappedQueryOptions = sortQueryOptions(fetchedQueries.map((queryItem) => mapQueryToOption(queryItem, fetchedApps)));
                const initialRows = mapContentsToRows(page?.contents);
                const unresolvedQueryIds = [...new Set(initialRows
                    .map((row) => row.queryId)
                    .filter((queryIdValue) => {
                        if (queryIdValue === '' || queryIdValue === null || queryIdValue === undefined) return false;
                        return !mappedQueryOptions.some((option) => String(option.id) === String(queryIdValue));
                    }))];

                const resolvedQueryEntries = await Promise.all(
                    unresolvedQueryIds.map(async (queryId) => {
                        try {
                            const queryResponse = await getQuery(queryId);
                            const queryData = queryResponse?.data || queryResponse;
                            if (!queryData) return null;

                            return {
                                sourceId: String(queryId),
                                option: mapQueryToOption(queryData, fetchedApps)
                            };
                        } catch {
                            return null;
                        }
                    })
                );

                const validResolvedEntries = resolvedQueryEntries.filter(Boolean);
                const extraQueryOptions = validResolvedEntries
                    .map((entry) => entry.option)
                    .filter((option) => !mappedQueryOptions.some((existing) => String(existing.id) === String(option.id)));

                const resolvedRows = initialRows.map((row) => {
                    const matchedOptionById = mappedQueryOptions.find((option) => String(option.id) === String(row.queryId));
                    if (matchedOptionById) {
                        return {
                            ...row,
                            queryId: matchedOptionById.id,
                            queryName: matchedOptionById.name
                        };
                    }

                    const matchedOptionByName = mappedQueryOptions.find((option) => option.name === row.queryName);
                    if (matchedOptionByName) {
                        return {
                            ...row,
                            queryId: matchedOptionByName.id,
                            queryName: matchedOptionByName.name
                        };
                    }

                    const matchedEntry = validResolvedEntries.find((entry) => entry.sourceId === String(row.queryId));
                    if (!matchedEntry) {
                        return row;
                    }

                    return {
                        ...row,
                        queryId: matchedEntry.option.id,
                        queryName: matchedEntry.option.name
                    };
                });

                const finalQueryOptions = sortQueryOptions([...mappedQueryOptions, ...extraQueryOptions]);
                const pageLayout = page?.layout || '';
                const pageLayoutId = page?.layout_id ?? '';
                const matchedLayoutIndex = fetchedLayouts.findIndex(
                    (layoutItem) => String(layoutItem?.id) === String(pageLayoutId)
                );

                setOriginalPage(page);
                setLayouts(fetchedLayouts);
                setQueryOptions(finalQueryOptions);
                setSelectedLayoutIndex(matchedLayoutIndex >= 0 ? String(matchedLayoutIndex) : '');
                setContentsRows(resolvedRows);
                setFormData({
                    label: page?.label || '',
                    layout_id: pageLayoutId,
                    layout: pageLayout,
                    url: page?.url || ''
                });
            } catch (err) {
                setError('Failed to load page data.');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePresetLayoutChange = (e) => {
        const selectedIndex = e.target.value;
        setSelectedLayoutIndex(selectedIndex);
        if (selectedIndex === '') return;

        const selectedLayout = layouts[Number(selectedIndex)];
        if (!selectedLayout) return;

        const layoutValue = selectedLayout.layout ?? selectedLayout;

        setFormData((prev) => ({
            ...prev,
            layout_id: selectedLayout.id ?? '',
            layout: layoutValue
        }));
    };

    const handleContentRowChange = (index, field, value) => {
        setContentsRows((prev) => prev.map((row, rowIndex) => {
            if (rowIndex !== index) return row;

            const nextRow = {
                ...row,
                [field]: value
            };

            if (field === 'type') {
                nextRow.view = getDefaultViewForType(value, row.view);
            }

            if (field !== 'type' || !row.queryId) {
                return nextRow;
            }

            const selectedQueryOption = queryOptions.find((option) => String(option.id) === String(row.queryId));
            const selectedQueryType = getQueryOptionType(selectedQueryOption);

            if (value && selectedQueryType && selectedQueryType !== value) {
                return {
                    ...nextRow,
                    queryId: '',
                    queryName: ''
                };
            }

            return nextRow;
        }));
    };

    const handleQuerySelectionChange = (index, selectedQueryId) => {
        const selectedOption = queryOptions.find((option) => String(option.id) === String(selectedQueryId));

        setContentsRows((prev) => prev.map((row, rowIndex) => {
            if (rowIndex !== index) return row;

            if (!selectedOption) {
                return {
                    ...row,
                    queryId: '',
                    queryName: ''
                };
            }

            return {
                ...row,
                type: row.type || getQueryOptionType(selectedOption),
                view: getDefaultViewForType(row.type || getQueryOptionType(selectedOption), row.view),
                queryId: selectedOption.id,
                queryName: selectedOption.name
            };
        }));
    };

    const handleAddContentRow = () => {
        setContentsRows((prev) => [...prev, createEmptyContentRow()]);
    };

    const handleRemoveContentRow = (index) => {
        setContentsRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        try {
            const parsedContents = contentsRows
                .map((row, index) => {
                    const trimmedParams = (row.params || '').trim();
                    let parsedParams = {};

                    if (trimmedParams !== '') {
                        try {
                            parsedParams = JSON.parse(trimmedParams);
                        } catch {
                            throw new Error(`Invalid JSON in params at row ${index + 1}`);
                        }
                    }

                    return {
                        type: row.type,
                        view: row.view,
                        queryId: row.queryId === '' ? null : Number(row.queryId),
                        queryName: row.queryName,
                        params: parsedParams
                    };
                })
                .filter((item) => (
                    item.type.trim() !== ''
                    || item.view.trim() !== ''
                    || item.queryId !== null
                    || item.queryName.trim() !== ''
                    || Object.keys(item.params || {}).length > 0
                ));

            const payload = {
                ...originalPage,
                label: formData.label,
                layout_id: formData.layout_id || null,
                // layout: formData.layout,
                contents: parsedContents.length > 0 ? { content: parsedContents } : null,
                url: formData.url
            };

            await updatePage(id, payload);
            navigate(getReturnPath());
        } catch (err) {
            if (err?.message?.includes('Invalid JSON in params')) {
                setError(err.message);
            } else {
                setError('Failed to save page.');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <GlobalLayout><div>Loading...</div></GlobalLayout>;
    }

    return (
        <GlobalLayout>
            <h1>Edit Page</h1>
            {error && <p style={{ color: 'red' }}>{error}</p>}

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="label">Label</label>
                    <input
                        id="label"
                        name="label"
                        type="text"
                        value={formData.label}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="layout">Layout</label>
                    {layouts.length > 0 && (
                        <div style={{ margin: '0.5rem 0' }}>
                            <select id="layout" value={selectedLayoutIndex} onChange={handlePresetLayoutChange} style={{ width: '100%', padding: '0.5rem' }}>
                                <option value="">-- Choisir un layout existant --</option>
                                {layouts.map((layoutItem, index) => (
                                    <option key={layoutItem.id || index} value={index}>
                                        {layoutItem.name || layoutItem.label || layoutItem.code || `Layout #${layoutItem.id || index + 1}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label>Contents</label>
                    <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Type</th>
                                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>View</th>
                                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Query</th>
                                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Params (JSON)</th>
                                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', width: '90px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contentsRows.length === 0 && (
                                    <tr>
                                        <td colSpan={5} style={{ border: '1px solid #ddd', padding: '0.75rem', color: '#666' }}>
                                            Aucun élément.
                                        </td>
                                    </tr>
                                )}
                                {contentsRows.map((row, index) => {
                                    const availableQueryOptions = getAvailableQueryOptions(queryOptions, row.type, row.queryId);
                                    const groupedQueryOptions = getGroupedQueryOptions(availableQueryOptions);

                                    return (
                                        <tr key={`content-row-${index}`}>
                                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                                <select
                                                    value={row.type}
                                                    onChange={(e) => handleContentRowChange(index, 'type', e.target.value)}
                                                    style={{ width: '100%', padding: '0.4rem' }}
                                                >
                                                    <option value="">-- Choisir --</option>
                                                    <option value="indic">indic</option>
                                                    <option value="chart">chart</option>
                                                    <option value="grid">grid</option>
                                                </select>
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                                <select
                                                    value={row.view}
                                                    onChange={(e) => handleContentRowChange(index, 'view', e.target.value)}
                                                    style={{ width: '100%', padding: '0.4rem' }}
                                                >
                                                    <option value="">-- Choisir --</option>
                                                    <option value="indic">indic</option>
                                                    <option value="bar">bar</option>
                                                    <option value="barLine">barLine</option>
                                                    <option value="line">line</option>
                                                    <option value="pie">pie</option>
                                                    <option value="stackedColumn">stackedColumn</option>
                                                    <option value="ssrm">ssrm</option>
                                                </select>
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                                <select
                                                    value={row.queryId === '' ? '' : String(row.queryId)}
                                                    onChange={(e) => handleQuerySelectionChange(index, e.target.value)}
                                                    style={{ width: '100%', padding: '0.4rem' }}
                                                >
                                                    <option value="">-- Choisir --</option>
                                                    {groupedQueryOptions.map((queryGroup) => (
                                                        <optgroup key={queryGroup.appLabel} label={queryGroup.appLabel}>
                                                            {queryGroup.options.map((queryOption, queryIndex) => (
                                                                <option key={queryOption.id || `${queryOption.value}-${queryIndex}`} value={queryOption.value}>
                                                                    {queryOption.queryLabel}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    ))}
                                                    {row.queryId !== '' && !availableQueryOptions.some((queryOption) => String(queryOption.id) === String(row.queryId)) && (
                                                        <option value={String(row.queryId)}>{row.queryName || `Query #${row.queryId}`}</option>
                                                    )}
                                                </select>
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                                <textarea
                                                    value={row.params}
                                                    onChange={(e) => handleContentRowChange(index, 'params', e.target.value)}
                                                    rows={3}
                                                    style={{ width: '100%', padding: '0.4rem' }}
                                                />
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>
                                                <button type="button" onClick={() => handleRemoveContentRow(index)}>
                                                    Supprimer
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ marginTop: '0.5rem' }}>
                        <button type="button" onClick={handleAddContentRow}>Ajouter une ligne</button>
                    </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="url">URL</label>
                    <input
                        id="url"
                        name="url"
                        type="text"
                        value={formData.url}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" onClick={() => navigate(getReturnPath())}>
                        Cancel
                    </button>
                </div>
            </form>
        </GlobalLayout>
    );
};

export default PageManagerEdit;
